import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgClass } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { InputComponent } from '../../../shared/components/input/input.component';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';
import { ButtonTypeEnum } from '../../../shared/components/button/types/button-type.enum';
import { ButtonSizeEnum } from '../../../shared/components/button/types/button-size.enum';
import { ButtonRadiusEnum } from '../../../shared/components/button/types/button-radius.enum';
import { SpinnerColorEnum } from '../../../shared/components/spinner/types/spinner-color.enum';
import { SpinnerSizeEnum } from '../../../shared/components/spinner/types/spinner-size.enum';
import { Tag } from '../../../shared/models/interfaces/tag.interface';
import { normalizeTagName, TAG_NAME_MAX_LENGTH, TagService } from '../../../shared/services/tag/tag.service';
import { ToastService } from '../../../shared/components/toast/service/toast.service';
import { getCrudErrorMessage } from '../../../shared/utils/functions';

export interface CreateTagModalData {
  /**
   * A tag que está sendo editada
   */
  tag?: Tag;

  /**
   * O nome sugerido ao abrir o modal (ex.: termo buscado no select de tags do curso)
   */
  initialName?: string;

  /**
   * Indica que a tag criada será adicionada às tags do curso em edição
   */
  addToCourse?: boolean;
}

export const TAG_DUPLICATED_MESSAGE = 'Já existe uma tag com esse nome.';

export const TAG_INACTIVE_MESSAGE = 'Já existe uma tag desativada com esse nome.';

interface CreateTagForm {
  name: FormControl<string>;
}

@Component({
  selector: 'l-create-tag-modal',
  standalone: true,
  templateUrl: './create-tag-modal.component.html',
  imports: [
    ReactiveFormsModule,
    NgClass,
    IconComponent,
    ButtonComponent,
    InputComponent,
    SpinnerComponent,
  ],
})
export class CreateTagModalComponent implements OnInit, OnDestroy {

  constructor(
    private readonly ref: DynamicDialogRef,
    private readonly config: DynamicDialogConfig<CreateTagModalData>,
    private readonly tagService: TagService,
    private readonly toastService: ToastService,
  ) {}

  public readonly nameMax = TAG_NAME_MAX_LENGTH;

  public readonly buttonTypeEnum = ButtonTypeEnum;

  public readonly buttonSizeEnum = ButtonSizeEnum;

  public readonly buttonRadiusEnum = ButtonRadiusEnum;

  public readonly spinnerColorEnum = SpinnerColorEnum;

  public readonly spinnerSizeEnum = SpinnerSizeEnum;

  public readonly formGroup = new FormGroup<CreateTagForm>({
    name: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(TAG_NAME_MAX_LENGTH), Validators.pattern(/\S/)],
    }),
  });

  public tag?: Tag;

  public isUpdate = false;

  public addToCourse = false;

  public isSubmitting = false;

  public isCheckingName = false;

  private readonly destroy$$ = new Subject<void>();

  public get nameControl(): FormControl<string> {
    return this.formGroup.controls.name;
  }

  public get modalTitle(): string {
    return this.isUpdate ? 'Editar tag' : 'Criar nova tag';
  }

  public get modalSubtitle(): string {
    if (this.isUpdate) {
      return 'Atualize o nome da tag de pesquisa';
    }

    return this.addToCourse
      ? 'A tag será criada e adicionada às tags deste curso'
      : 'Crie uma nova tag para facilitar a busca dos cursos';
  }

  public get submitLabel(): string {
    if (this.isUpdate) {
      return 'Salvar';
    }

    return this.addToCourse ? 'Criar e adicionar' : 'Criar';
  }

  public get nameCount(): number {
    return this.nameControl.value.length;
  }

  public get conflictMessage(): string {
    const conflict = this.nameControl.errors?.['conflict'];
    return typeof conflict === 'string' ? conflict : '';
  }

  public get errorMessage(): string {
    if (this.conflictMessage) {
      return this.conflictMessage;
    }

    if (!this.nameControl.touched && !this.nameControl.dirty) {
      return '';
    }

    if (this.nameControl.hasError('required') || this.nameControl.hasError('pattern')) {
      return 'O nome da tag é obrigatório.';
    }

    if (this.nameControl.hasError('maxlength')) {
      return `O nome da tag deve ter no máximo ${TAG_NAME_MAX_LENGTH} caracteres.`;
    }

    return '';
  }

  public get isDisabled(): boolean {
    return this.formGroup.invalid || this.isSubmitting || this.isCheckingName || !this.hasChanges;
  }

  private get hasChanges(): boolean {
    return !this.isUpdate || normalizeTagName(this.nameControl.value) !== this.tag?.name;
  }

  public ngOnInit(): void {
    const data = this.config.data;

    this.tag = data?.tag;
    this.isUpdate = !!data?.tag;
    this.addToCourse = data?.addToCourse === true;

    this.nameControl.valueChanges.pipe(
      debounceTime(400),
      takeUntil(this.destroy$$),
    ).subscribe((value) => {
      void this.checkNameAvailability(value);
    });

    const initialName = this.tag?.name ?? data?.initialName?.trim().slice(0, TAG_NAME_MAX_LENGTH) ?? '';

    if (initialName) {
      this.nameControl.setValue(initialName);
      this.nameControl.markAsDirty();
    }
  }

  public ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.complete();
  }

  public close(): void {
    this.ref.close();
  }

  public async submit(): Promise<void> {
    this.formGroup.markAllAsTouched();

    if (this.isDisabled) {
      return;
    }

    try {
      this.isSubmitting = true;

      const payload = { name: this.nameControl.value };
      const saved = this.isUpdate && this.tag
        ? await this.tagService.update(this.tag.id, payload)
        : await this.tagService.create(payload);

      this.ref.close(saved);
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 409) {
        this.setConflict(getCrudErrorMessage(error, TAG_DUPLICATED_MESSAGE));
        return;
      }

      this.toastService.error(getCrudErrorMessage(error), 'Oops...');
    } finally {
      this.isSubmitting = false;
    }
  }

  private async checkNameAvailability(value: string): Promise<void> {
    const name = normalizeTagName(value);

    if (!name || name.length > TAG_NAME_MAX_LENGTH || name === this.tag?.name) {
      return;
    }

    try {
      this.isCheckingName = true;

      const existing = await this.tagService.findByName(name);

      // Ignora respostas de buscas antigas, caso o usuário tenha continuado digitando
      if (normalizeTagName(this.nameControl.value) !== name || !existing || existing.id === this.tag?.id) {
        return;
      }

      this.setConflict(existing.isActive ? TAG_DUPLICATED_MESSAGE : TAG_INACTIVE_MESSAGE);
    } catch {
      // A validação definitiva acontece na API ao salvar
    } finally {
      this.isCheckingName = false;
    }
  }

  private setConflict(message: string): void {
    this.nameControl.setErrors({ ...this.nameControl.errors, conflict: message });
    this.nameControl.markAsTouched();
  }
}
